import type { PrV2ChecksDisplay } from './check-run-presentation';
import type { PrV2CheckRunResponse } from './types';

import { classifyPrV2CheckRun } from './check-run-presentation';

export type ChecksTone = 'failed' | 'running' | 'waiting' | 'passed';

export type CheckNameParts = { prefix: string | null; job: string };

/** Show an explicit workflow or a provider proved by the check's details URL. */
export function getCheckNameParts(
  check: Pick<PrV2CheckRunResponse, 'name' | 'detailsUrl'>,
): CheckNameParts {
  const separator = check.name.indexOf(' / ');
  if (separator > 0) {
    const prefix = check.name.slice(0, separator).trim();
    const job = check.name.slice(separator + 3).trim();
    if (prefix && job) {
      return { prefix, job };
    }
  }

  if (!check.detailsUrl) {
    return { prefix: null, job: check.name };
  }
  let url: URL;
  try {
    url = new URL(check.detailsUrl);
  } catch {
    return { prefix: null, job: check.name };
  }
  const host = url.hostname.toLowerCase();
  let provider: string | null = null;
  if (host === 'github.com' && /^\/[^/]+\/[^/]+\/actions\/runs\/\d+(?:\/|$)/.test(url.pathname)) {
    provider = 'CI';
  } else if (host === 'vercel.com' || host.endsWith('.vercel.com')) {
    provider = 'Vercel';
  } else if (host === 'cursor.com' || host.endsWith('.cursor.com')) {
    provider = 'Cursor';
  }
  if (!provider) {
    return { prefix: null, job: check.name };
  }

  const normalizedName = check.name.trim().toLowerCase();
  const normalizedProvider = provider.toLowerCase();
  if (normalizedName === normalizedProvider) {
    return { prefix: null, job: check.name };
  }
  if (normalizedName.startsWith(`${normalizedProvider} `)) {
    return { prefix: provider, job: check.name.trim().slice(provider.length).trimStart() };
  }
  return { prefix: provider, job: check.name };
}

/** Summarize only the current-head, deduplicated runs supplied by buildPrV2ChecksDisplay. */
export function getChecksTone({
  counts,
  pendingRequiredChecks,
}: Pick<PrV2ChecksDisplay, 'counts' | 'pendingRequiredChecks'>): ChecksTone {
  if (counts.failed > 0) {
    return 'failed';
  }

  if (counts.running > 0) {
    return 'running';
  }

  if (pendingRequiredChecks.length > 0) {
    return 'waiting';
  }

  // Every run left is successful, neutral or skipped, which GitHub and the inbox count as passing.
  return 'passed';
}

export function formatCheckDuration(check: PrV2CheckRunResponse): string | null {
  if (check.status !== 'completed' || !check.startedAt || !check.completedAt) {
    return null;
  }

  const start = Date.parse(check.startedAt);
  const end = Date.parse(check.completedAt);
  if (!Number.isFinite(start) || !Number.isFinite(end) || end < start) {
    return null;
  }

  const seconds = Math.round((end - start) / 1000);
  if (seconds < 60) {
    return `${seconds}s`;
  }

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return remainingSeconds === 0 ? `${minutes}m` : `${minutes}m ${remainingSeconds}s`;
}

export type CheckListItem =
  | { kind: 'run'; check: PrV2CheckRunResponse; required: boolean }
  | { kind: 'expected'; name: string; required: true };

/** Group required runs and expectations before the other current-head runs. */
export function getChecksListItems(display: PrV2ChecksDisplay): CheckListItem[] {
  const required = display.sortedChecks
    .filter((check) => display.requiredCheckNames.has(check.name))
    .map((check): CheckListItem => ({ kind: 'run', check, required: true }));
  const expected = display.pendingRequiredChecks.map((name): CheckListItem => ({
    kind: 'expected',
    name,
    required: true,
  }));
  const others = display.sortedChecks
    .filter((check) => !display.requiredCheckNames.has(check.name))
    .map((check): CheckListItem => ({ kind: 'run', check, required: false }));

  return [...required, ...expected, ...others];
}

export function isCheckItemAttention(item: CheckListItem): boolean {
  if (item.kind === 'expected') {
    return true;
  }
  const bucket = classifyPrV2CheckRun(item.check);
  return bucket === 'failed' || bucket === 'running';
}
